import React from 'react';
import { useCluster } from 'src/features/cluster';
import { StatusProgramId } from 'src/constants/status-program';
import { ProgramRendererType } from 'src/types/ProgramRendererType';
import { ClusterStatusListItem, StatusProgramService } from 'src/api/v2';
import { useQuery } from '@tanstack/react-query';
import { STATUS_PROGRAMS_KEY } from 'src/constants/hook-keys';

const useStatusPrograms = () => {
  const [statusProgramService] = React.useState(new StatusProgramService());
  const { cluster } = useCluster();

  const statusProgramsQuery = useQuery({
    queryKey: [STATUS_PROGRAMS_KEY],
    queryFn: () => statusProgramService.getStatusProgramList(),
    retry: false,
    staleTime: 1000 * 60 * 30, // 30 min refetching on mount
    refetchOnMount: true,
  });

  const { clusterStatuses, owiStatuses, sivaStatuses } = React.useMemo(() => {
    if (!statusProgramsQuery.data) {
      return {
        clusterStatuses: [] as ClusterStatusListItem[],
        owiStatuses: [] as ClusterStatusListItem[],
        sivaStatuses: [] as ClusterStatusListItem[],
      };
    }

    const clusterStatuses =
      statusProgramsQuery.data?.find((statusProgram) => statusProgram.id === StatusProgramId.Cluster)
        ?.clusterStatuses || [];
    const owiStatuses =
      statusProgramsQuery.data?.find((statusProgram) => statusProgram.id === StatusProgramId.OneWayIn)
        ?.clusterStatuses || [];
    const sivaStatusesIds =
      statusProgramsQuery.data?.find((statusProgram) => statusProgram.id === StatusProgramId.Siva)
        ?.clusterStatuses || [];

    return {
      clusterStatuses: clusterStatuses,
      owiStatuses: owiStatuses,
      sivaStatuses: sivaStatusesIds,
    };
  }, [statusProgramsQuery.data]);

  const clusterStatusRulesMap: Record<ProgramRendererType, string[]> = React.useMemo(() => {
    return {
      cluster: [...clusterStatuses.map((status) => status.id)],
      'one-way-in': [...owiStatuses.map((status) => status.id)],
      siva: [...sivaStatuses.map((status) => status.id)],

      '*': [], // Trenger ingen i seg, denne gjelder for alle, eller som catcher
    };
  }, [clusterStatuses, owiStatuses, sivaStatuses]);

  const isProgramSiva = React.useMemo(() => {
    if (!cluster) return false;

    if (cluster.clusterStatusId && clusterStatusRulesMap.siva.includes(cluster.clusterStatusId)) return true;

    return false;
  }, [cluster, clusterStatusRulesMap]);

  const isProgramOneWayIn = React.useMemo(() => {
    if (!cluster) return false;

    if (cluster.clusterStatusId && clusterStatusRulesMap['one-way-in'].includes(cluster.clusterStatusId))
      return true;

    return false;
  }, [cluster, clusterStatusRulesMap]);

  const isProgramCluster = React.useMemo(() => {
    if (!cluster) return false;

    if (cluster.clusterStatusId && clusterStatusRulesMap.cluster.includes(cluster.clusterStatusId))
      return true;

    return false;
  }, [cluster, clusterStatusRulesMap.cluster]);

  return {
    statusProgramsQuery,
    clusterStatusRulesMap,
    isProgramSiva,
    isProgramOneWayIn,
    isProgramCluster,
  };
};

export default useStatusPrograms;
